import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import axios from 'axios';
import Logo from '../../components/Logo';
import {
  Clock,
  ChevronLeft,
  ChevronRight,
  Upload,
  FileText,
  CheckCircle,
  AlertCircle,
  User,
  Mail,
  X
} from 'lucide-react';

const StudentAssessment = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  // States
  const [student, setStudent] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSection, setCurrentSection] = useState(1);
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [started, setStarted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dragHover, setDragHover] = useState({});

  // Handle file download
  const handleFileDownload = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();

      // Create a temporary URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);

      // Create a temporary download link
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.style.display = 'none';

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Download failed. Please try again.');
    }
  };
  const [startTime, setStartTime] = useState(null);
  const [previousAttempt, setPreviousAttempt] = useState(null);

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm();

  // Load student and assessment data
  useEffect(() => {
    loadAssessmentData();
  }, [token]);

  // Timer effect
  useEffect(() => {
    if (started && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleTimeUp();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [started, timeLeft]);

  const loadAssessmentData = async () => {
    try {
      // Load student info
      const studentResponse = await axios.get(`/api/student/${token}`);
      setStudent(studentResponse.data.student);

      // Load assessment
      const assessmentResponse = await axios.get(`/api/student/${token}/assessment`);

      // Check if assessment is already completed
      if (assessmentResponse.data.alreadyCompleted) {
        toast.success('Assessment already completed!');
        navigate(`/assessment/${token}/complete`, {
          state: { result: assessmentResponse.data.result }
        });
        return;
      }


      setAssessment(assessmentResponse.data.assessment);
      setPreviousAttempt(assessmentResponse.data.previousAttempt);

      if (assessmentResponse.data.assessment.timeLimit) {
        setTimeLeft(assessmentResponse.data.assessment.timeLimit * 60); // Convert to seconds
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Invalid or expired invitation link');
        navigate('/');
      } else {
        toast.error('Failed to load assessment');
      }
    } finally {
      setLoading(false);
    }
  };

  const startAssessment = async () => {
    try {


      await axios.post(`/api/student/${token}/start`);

      // Update all states together to ensure proper rendering
      const startTimeValue = new Date();
      setStarted(true);
      setStartTime(startTimeValue);
      setCurrentSection(1);



      // Scroll to top when assessment starts
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.success('Assessment started. Good luck!');
    } catch (error) {
      toast.error('Failed to start assessment');
    }
  };

  // Scroll to top when section changes
  useEffect(() => {
    if (started) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentSection, started]);

  const handleTimeUp = useCallback(() => {
    toast.error('Time is up! Submitting your assessment automatically.');
    submitAssessment();
  }, []);

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleFileChange = (questionId, file) => {
    setFiles(prev => ({
      ...prev,
      [questionId]: file
    }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e, questionId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragHover(prev => ({ ...prev, [questionId]: true }));
  };

  const handleDragLeave = (e, questionId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragHover(prev => ({ ...prev, [questionId]: false }));
  };

  const handleDrop = (e, questionId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragHover(prev => ({ ...prev, [questionId]: false }));

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles[0]) {
      handleFileChange(questionId, droppedFiles[0]);
    }
  };

  const submitAssessment = async (formData = {}) => {
    setSubmitting(true);

    try {
      const submission = new FormData();
      // Include both regular answers and file upload answers
      const answerArray = Object.entries(answers).map(([questionId, answer]) => ({
        questionId,
        answer
      }));

      // Add file upload answers
      Object.entries(files).forEach(([questionId, file]) => {
        if (file) {
          answerArray.push({
            questionId,
            answer: 'file_uploaded'
          });
        }
      });

      const submissionData = {
        answers: answerArray,
        timeSpent: Math.floor((new Date() - startTime) / 60000), // minutes
        startedAt: startTime.toISOString()
      };

      // If there are files, we need to use FormData
      if (Object.keys(files).length > 0) {
        submission.append('answers', JSON.stringify(answerArray));
        submission.append('timeSpent', Math.floor((new Date() - startTime) / 60000));
        submission.append('startedAt', startTime.toISOString());

        // Add files
        Object.entries(files).forEach(([questionId, file]) => {
          if (file) {
            submission.append(`files`, file);
          }
        });

        const response = await axios.post(`/api/student/${token}/submit`, submission, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        toast.success('Assessment submitted successfully!');
        // Scroll to top before navigation
        window.scrollTo({ top: 0, behavior: 'smooth' });
        navigate(`/assessment/${token}/complete`, {
          state: { result: response.data.result }
        });
      } else {
        // No files, send as JSON
        const response = await axios.post(`/api/student/${token}/submit`, submissionData, {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        toast.success('Assessment submitted successfully!');
        // Scroll to top before navigation
        window.scrollTo({ top: 0, behavior: 'smooth' });
        navigate(`/assessment/${token}/complete`, {
          state: { result: response.data.result }
        });
      }
    } catch (error) {
      toast.error('Failed to submit assessment');
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getSectionQuestions = (sectionId) => {
    const questions = assessment?.questions || [];
    // Fix: Convert both to strings for comparison since questions have string section IDs
    return questions.filter(q => String(q.section) === String(sectionId)) || [];
  };

  const getCurrentSectionQuestions = () => {
    const questions = getSectionQuestions(currentSection);
    return questions;
  };

  const isCurrentSectionComplete = () => {
    const sectionQuestions = getCurrentSectionQuestions();
    return sectionQuestions.every(q => {
      if (q.type === 'file_upload') {
        return files[q.id] || !q.required;
      }
      return answers[q.id] || !q.required;
    });
  };

  const getTotalProgress = () => {
    const totalQuestions = assessment?.questions.length || 0;
    const answeredQuestions = Object.keys(answers).length + Object.keys(files).length;
    return totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;
  };

  if (loading) {
    return (
      <div className="assessment-container">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (!started) {
    return (
      <div className="assessment-container">
        <div className="container">
          <div className="welcome-content">
            <div className="welcome-header">
              <Logo size="large" />
              <h1>Distance Education Readiness Assessment</h1>
            </div>

            <motion.div
              className="welcome-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="student-info">
                <div className="info-item">
                  <User size={20} />
                  <span>{student?.name}</span>
                </div>
                <div className="info-item">
                  <Mail size={20} />
                  <span>{student?.email}</span>
                </div>
              </div>

              {previousAttempt && (
                <div className="previous-attempt">
                  <h3>Previous Attempt</h3>
                  <div className="attempt-info">
                    <div className="attempt-score">
                      <span className={`score ${previousAttempt.passed ? 'passed' : 'failed'}`}>
                        {previousAttempt.percentage}%
                      </span>
                      <span className={previousAttempt.passed ? 'text-success' : 'text-danger'}>
                        {previousAttempt.passed ? 'Passed' : 'Failed'}
                      </span>
                    </div>
                    <p>Completed on {new Date(previousAttempt.completedAt).toLocaleDateString()}</p>
                    {!previousAttempt.passed && (
                      <p className="retake-note">You can retake this assessment to improve your score.</p>
                    )}
                  </div>
                </div>
              )}

              <div className="assessment-info">
                <h2>{assessment?.title}</h2>
                <p>{assessment?.description}</p>

                <div className="assessment-details">
                  <div className="detail-item">
                    <Clock size={20} />
                    <span>Time Limit: {assessment?.timeLimit} minutes</span>
                  </div>
                  <div className="detail-item">
                    <FileText size={20} />
                    <span>{assessment?.questions?.length} questions across {assessment?.sections?.length} sections</span>
                  </div>
                  <div className="detail-item">
                    <CheckCircle size={20} />
                    <span>Passing Score: {assessment?.passingScore}%</span>
                  </div>
                </div>

                <div className="sections-preview">
                  <h3>Assessment Sections:</h3>
                  {assessment?.sections?.map(section => (
                    <div key={section.id} className="section-preview">
                      <h4>Section {section.id}: {section.title}</h4>
                      <p>{section.description}</p>
                      <span className="question-count">
                        {getSectionQuestions(section.id).length} questions
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="start-actions">
                <button
                  className="btn btn-primary btn-lg"
                  onClick={startAssessment}
                >
                  <CheckCircle size={20} />
                  Start Assessment
                </button>
              </div>
            </motion.div>
          </div>
        </div>

        <style jsx>{`
          .assessment-container {
            min-height: 100vh;
            background: linear-gradient(135deg, var(--primary-bg) 0%, #d6e7ff 100%);
            padding: 40px 20px;
          }

          .welcome-content {
            max-width: 800px;
            margin: 0 auto;
          }

          .welcome-header {
            text-align: center;
            margin-bottom: 40px;
          }

          .welcome-header h1 {
            font-size: 32px;
            color: var(--primary-accent);
            margin-top: 24px;
            font-weight: 700;
            font-family: 'Plus Jakarta Sans', sans-serif;
          }

          .welcome-card {
            background: var(--white);
            border-radius: var(--border-radius-lg);
            box-shadow: var(--shadow-lg);
            padding: 40px;
          }

          .student-info {
            display: flex;
            gap: 24px;
            margin-bottom: 32px;
            padding: 20px;
            background-color: var(--gray-100);
            border-radius: var(--border-radius);
          }

          .info-item {
            display: flex;
            align-items: center;
            gap: 8px;
            color: var(--text-primary);
            font-weight: 500;
          }

          .previous-attempt {
            background-color: var(--primary-bg);
            padding: 24px;
            border-radius: var(--border-radius);
            margin-bottom: 32px;
          }

          .previous-attempt h3 {
            color: var(--primary-accent);
            margin-bottom: 16px;
          }

          .attempt-score {
            display: flex;
            align-items: center;
            gap: 16px;
            margin-bottom: 8px;
          }

          .score {
            font-size: 24px;
            font-weight: 700;
          }

          .score.passed {
            color: var(--success);
          }

          .score.failed {
            color: var(--danger);
          }

          .assessment-info h2 {
            color: var(--primary-accent);
            margin-bottom: 16px;
            font-size: 24px;
          }

          .assessment-info p {
            color: var(--text-secondary);
            margin-bottom: 24px;
            font-size: 16px;
            line-height: 1.6;
          }

          .assessment-details {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 16px;
            margin-bottom: 32px;
          }

          .detail-item {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 16px;
            background-color: var(--gray-100);
            border-radius: var(--border-radius);
            color: var(--text-primary);
          }

          .sections-preview h3 {
            color: var(--text-primary);
            margin-bottom: 16px;
          }

          .section-preview {
            padding: 16px;
            border: 1px solid var(--gray-300);
            border-radius: var(--border-radius);
            margin-bottom: 12px;
          }

          .section-preview h4 {
            color: var(--primary-accent);
            margin-bottom: 8px;
          }

          .section-preview p {
            color: var(--text-secondary);
            font-size: 14px;
            margin-bottom: 8px;
          }

          .question-count {
            font-size: 12px;
            background-color: var(--primary-accent);
            color: var(--white);
            padding: 4px 8px;
            border-radius: 12px;
          }

          .start-actions {
            text-align: center;
            margin-top: 32px;
          }

          @media (max-width: 768px) {
            .student-info {
              flex-direction: column;
              gap: 16px;
            }

            .assessment-details {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
      </div>
    );
  }

  // Assessment Interface
  return (
    <div className="assessment-interface">
      {/* Fixed Header */}
      <div className="assessment-header">
        <div className="header-left">
          <Logo size="small" />
          <div className="student-name">{student?.name}</div>
        </div>

        <div className="header-center">
          <div className="progress-info">
            <span>Progress: {Math.round(getTotalProgress())}%</span>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${getTotalProgress()}%` }}
              />
            </div>
          </div>
        </div>

        <div className="timer">
          <Clock size={16} />
          <span>{formatTime(timeLeft)}</span>
        </div>
      </div>

      {/* Section Navigation - Mobile dropdown, Desktop tabs */}
      <div className="section-nav-container">
        {/* Mobile Dropdown */}
        <div className="mobile-section-nav">
          <select
            value={currentSection}
            onChange={(e) => setCurrentSection(Number(e.target.value))}
            className="form-control"
          >
            {assessment?.sections?.map(section => (
              <option key={section.id} value={section.id}>
                Section {section.id}: {section.title}
              </option>
            ))}
          </select>
        </div>

        {/* Desktop Tabs */}
        <div className="desktop-section-nav">
          {assessment?.sections?.map(section => (
            <button
              key={section.id}
              onClick={() => setCurrentSection(section.id)}
              className={`section-tab ${currentSection === section.id ? 'active' : ''}`}
            >
              <span className="section-number">Section {section.id}</span>
              <span className="section-title">{section.title}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="section-content">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSection}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="section-header">
              <h2>{assessment?.sections?.find(s => s.id === currentSection)?.title}</h2>
              <p>{assessment?.sections?.find(s => s.id === currentSection)?.description}</p>
            </div>

            <form>
              {getCurrentSectionQuestions().map((question, index) => (
                <motion.div
                  key={question.id}
                  className="question-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="question-header">
                    <h3>Question {index + 1}</h3>
                    {question.required && (
                      <span className="required">Required</span>
                    )}
                  </div>

                  <p className="question-text">{question.question}</p>

                  {/* Yes/No Questions */}
                  {question.type === 'yes_no' && (
                    <div className="answer-options">
                      <label className="radio-option">
                        <input
                          type="radio"
                          name={question.id}
                          value="yes"
                          checked={answers[question.id] === 'yes'}
                          onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        />
                        <span className="radio-custom"></span>
                        <span>Yes</span>
                      </label>
                      <label className="radio-option">
                        <input
                          type="radio"
                          name={question.id}
                          value="no"
                          checked={answers[question.id] === 'no'}
                          onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        />
                        <span className="radio-custom"></span>
                        <span>No</span>
                      </label>
                    </div>
                  )}

                  {/* Multiple Choice Questions */}
                  {question.type === 'multiple_choice' && (
                    <div className="answer-options">
                      {question.options?.map(option => (
                        <label key={option.id} className="radio-option">
                          <input
                            type="radio"
                            name={question.id}
                            value={option.id}
                            checked={answers[question.id] === option.id}
                            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                          />
                          <span className="radio-custom"></span>
                          <span>{option.text}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {/* File Upload Questions */}
                  {question.type === 'file_upload' && (
                    <div className="file-upload-area">
                      {question.hasDownloadableFile && question.downloadableFile && (
                        <div className="downloadable-file-section">
                          <h4>Download Required File:</h4>
                          <div className="download-preview">
                            {question.downloadableFile.mimetype?.startsWith('image/') && (
                              <img
                                src={question.downloadableFile.url}
                                alt={question.downloadableFile.originalName}
                                className="download-preview-image"
                              />
                            )}
                            <div className="download-info">
                              <p><strong>{question.downloadableFile.originalName}</strong></p>
                              <button
                                onClick={() => handleFileDownload(question.downloadableFile.url, question.downloadableFile.originalName)}
                                className="download-button"
                                type="button"
                              >
                                📥 Download File
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      {question.fileInstructions && (
                        <div className="upload-instructions">
                          <AlertCircle size={16} />
                          <span>{question.fileInstructions}</span>
                        </div>
                      )}

                      <div
                        className={`upload-zone ${dragHover[question.id] ? 'drag-hover' : ''}`}
                        onDragOver={handleDragOver}
                        onDragEnter={(e) => handleDragEnter(e, question.id)}
                        onDragLeave={(e) => handleDragLeave(e, question.id)}
                        onDrop={(e) => handleDrop(e, question.id)}
                        onClick={() => document.getElementById(`file-${question.id}`).click()}
                      >
                        <input
                          type="file"
                          id={`file-${question.id}`}
                          className="file-input"
                          onChange={(e) => handleFileChange(question.id, e.target.files[0])}
                          accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                        />
                        <label htmlFor={`file-${question.id}`} className="upload-label">
                          <Upload size={24} />
                          <span>
                            {files[question.id]
                              ? files[question.id].name
                              : 'Click to upload file or drag and drop'
                            }
                          </span>
                        </label>
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </form>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Fixed Footer Navigation */}
      <div className="assessment-footer">
        <div className="footer-left">
          {currentSection > 1 && (
            <button
              className="btn btn-secondary"
              onClick={() => setCurrentSection(currentSection - 1)}
            >
              <ChevronLeft size={16} />
              Previous Section
            </button>
          )}
        </div>

        <div className="footer-center">
          <span className="section-indicator">
            Section {currentSection} of {assessment?.sections?.length}
          </span>
        </div>

        <div className="footer-right">
          {currentSection < assessment?.sections?.length ? (
            <button
              className="btn btn-primary"
              onClick={() => setCurrentSection(currentSection + 1)}
              disabled={!isCurrentSectionComplete()}
            >
              Next Section
              <ChevronRight size={16} />
            </button>
          ) : (
            <button
              className="btn btn-success"
              onClick={handleSubmit(submitAssessment)}
              disabled={submitting || !isCurrentSectionComplete()}
            >
              {submitting ? (
                <div className="loading-spinner" style={{ width: '16px', height: '16px' }} />
              ) : (
                <>
                  <CheckCircle size={16} />
                  Submit Assessment
                </>
              )}
            </button>
          )}
        </div>
      </div>

      <style jsx>{`
        .assessment-interface {
          display: flex;
          flex-direction: column;
          min-height: 100vh;
          background-color: var(--primary-bg);
        }

        .assessment-header {
          background-color: var(--white);
          padding: 16px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          box-shadow: var(--shadow-sm);
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .student-name {
          font-weight: 600;
          color: var(--text-primary);
        }

        .header-center {
          flex: 1;
          display: flex;
          justify-content: center;
        }

        .progress-info {
          display: flex;
          align-items: center;
          gap: 12px;
          color: var(--text-secondary);
          font-size: 14px;
        }

        .progress-bar {
          width: 150px;
          height: 6px;
          background-color: var(--gray-200);
          border-radius: 3px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background-color: var(--primary-accent);
          transition: width 0.3s ease;
        }

        .timer {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background-color: var(--gray-100);
          border-radius: 20px;
          font-weight: 600;
          color: var(--text-primary);
          font-family: monospace;
        }

        .section-nav-container {
          background-color: var(--white);
          border-bottom: 1px solid var(--gray-200);
        }

        .mobile-section-nav {
          display: block;
          padding: 16px 24px;
        }

        .desktop-section-nav {
          display: none;
        }

        .section-tab {
          flex: 1;
          padding: 16px 20px;
          border: none;
          background: none;
          border-bottom: 3px solid transparent;
          cursor: pointer;
          transition: var(--transition);
          text-align: center;
        }

        .section-tab:hover {
          background-color: var(--gray-100);
        }

        .section-tab.active {
          border-bottom-color: var(--primary-accent);
          background-color: var(--primary-bg);
        }

        .section-number {
          display: block;
          font-weight: 700;
          color: var(--primary-accent);
          margin-bottom: 4px;
        }

        .section-title {
          display: block;
          font-size: 14px;
          color: var(--text-secondary);
        }

        .section-content {
          flex: 1;
          padding: 32px 24px 120px 24px; /* Extra bottom padding for fixed footer */
          max-width: 800px;
          margin: 0 auto;
          width: 100%;
        }

        .section-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .section-header h2 {
          color: var(--primary-accent);
          margin-bottom: 8px;
          font-size: 28px;
        }

        .section-header p {
          color: var(--text-secondary);
          font-size: 16px;
        }

        .question-card {
          background-color: var(--white);
          border-radius: var(--border-radius-lg);
          padding: 24px;
          margin-bottom: 24px;
          box-shadow: var(--shadow-sm);
          border: 1px solid var(--gray-200);
        }

        .question-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .question-header h3 {
          color: var(--text-primary);
          font-size: 18px;
          margin: 0;
        }

        .required {
          background-color: var(--danger);
          color: var(--white);
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
        }

        .question-text {
          color: var(--text-primary);
          font-size: 16px;
          line-height: 1.6;
          margin-bottom: 20px;
        }

        .answer-options {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .radio-option {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          border: 2px solid var(--gray-200);
          border-radius: var(--border-radius);
          cursor: pointer;
          transition: var(--transition);
          position: relative;
        }

        .radio-option:hover {
          border-color: var(--primary-accent);
        }

        .radio-option input[type="radio"]:checked + .radio-custom {
          background-color: var(--primary-accent);
          border-color: var(--primary-accent);
        }

        .radio-option input[type="radio"]:checked + .radio-custom:after {
          transform: translate(-50%, -50%) scale(1);
        }

        .radio-custom {
          width: 20px;
          height: 20px;
          border: 2px solid var(--gray-300);
          border-radius: 50%;
          position: relative;
          transition: var(--transition);
          display: inline-block;
          flex-shrink: 0;
        }

        .radio-custom:after {
          content: '';
          width: 8px;
          height: 8px;
          background-color: var(--white);
          border-radius: 50%;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) scale(0);
          transition: transform 0.2s ease;
        }

        input[type="radio"] {
          display: none;
        }

        .file-upload-area {
          margin-top: 16px;
        }

        .upload-instructions {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background-color: var(--info);
          color: var(--white);
          border-radius: var(--border-radius);
          margin-bottom: 16px;
          font-size: 14px;
        }

        .upload-zone {
          border: 2px dashed var(--gray-300);
          border-radius: var(--border-radius);
          padding: 32px;
          text-align: center;
          transition: var(--transition);
          cursor: pointer;
        }

        .upload-zone:hover {
          border-color: var(--primary-accent);
        }

        .file-input {
          display: none;
        }

        .upload-label {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          color: var(--text-secondary);
        }

        .assessment-footer {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background-color: var(--white);
          border-top: 1px solid var(--gray-200);
          padding: 16px 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
          z-index: 100;
        }

        .footer-left {
          flex: 1;
        }

        .footer-center {
          flex: 1;
          text-align: center;
        }

        .footer-right {
          flex: 1;
          display: flex;
          justify-content: flex-end;
        }

        .section-indicator {
          color: var(--text-secondary);
          font-weight: 500;
        }

        /* Desktop styles */
        @media (min-width: 768px) {
          .mobile-section-nav {
            display: none;
          }

          .desktop-section-nav {
            display: flex;
          }

          .answer-options {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 16px;
          }
        }

        /* Mobile responsive */
        @media (max-width: 767px) {
          .assessment-header {
            padding: 12px 16px;
          }

          .header-center {
            display: none;
          }

          .section-content {
            padding: 20px 16px 120px 16px;
          }

          .assessment-footer {
            padding: 12px 16px;
            flex-direction: column;
            gap: 12px;
          }

          .footer-left,
          .footer-center,
          .footer-right {
            flex: none;
            width: 100%;
            text-align: center;
          }
        }

        /* Downloadable File Styles */
        .downloadable-file-section {
          background: var(--primary-bg);
          padding: 20px;
          border-radius: var(--border-radius);
          margin-bottom: 20px;
          border: 2px solid var(--primary-accent);
        }

        .downloadable-file-section h4 {
          color: var(--primary-accent);
          margin-bottom: 16px;
          font-weight: 600;
        }

        .download-preview {
          display: flex;
          gap: 16px;
          align-items: center;
          background: var(--white);
          padding: 16px;
          border-radius: var(--border-radius);
          border: 1px solid var(--gray-200);
        }

        .download-preview-image {
          width: 80px;
          height: 80px;
          object-fit: contain;
          border-radius: var(--border-radius);
          border: 1px solid var(--gray-200);
        }

        .download-info {
          flex: 1;
        }

        .download-info p {
          margin: 0 0 12px 0;
          color: var(--text-primary);
        }

        .download-button {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: var(--primary-accent);
          color: var(--white);
          padding: 8px 16px;
          border: none;
          border-radius: var(--border-radius);
          text-decoration: none;
          font-weight: 500;
          font-size: 14px;
          font-family: inherit;
          cursor: pointer;
          transition: var(--transition);
        }

        .download-button:hover {
          background: #0041cc;
          transform: translateY(-1px);
        }

        .download-button:active {
          transform: translateY(0);
        }

        /* File Upload Zone Styles */
        .file-upload-area {
          margin-top: 16px;
        }

        .upload-zone {
          position: relative;
          border: 2px dashed var(--gray-300);
          border-radius: var(--border-radius);
          padding: 40px 20px;
          text-align: center;
          background: var(--gray-50);
          transition: all 0.2s ease;
          cursor: pointer;
        }

        .upload-zone:hover {
          border-color: var(--primary-accent);
          background: var(--primary-bg);
        }

        .upload-zone.drag-hover {
          border-color: var(--primary-accent);
          background: var(--primary-bg);
          transform: scale(1.02);
          box-shadow: 0 4px 12px rgba(0, 89, 255, 0.1);
        }

        .file-input {
          position: absolute;
          opacity: 0;
          width: 100%;
          height: 100%;
          cursor: pointer;
        }

        .upload-label {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          color: var(--text-secondary);
          font-weight: 500;
          cursor: pointer;
          pointer-events: none;
        }

        .upload-label span {
          font-size: 16px;
        }

        .upload-instructions {
          display: flex;
          align-items: center;
          gap: 8px;
          background: var(--warning-bg);
          color: var(--warning);
          padding: 12px 16px;
          border-radius: var(--border-radius);
          margin-bottom: 16px;
          font-size: 14px;
        }
      `}</style>
    </div>
  );
};

export default StudentAssessment;